import { AppAccent } from 'src/app/core/types/app-accent.type';
import { AppVisualContextService } from 'src/app/shared/services/app-visual-context.service';
import { HostBinding, inject, Component, DestroyRef, Input, ViewChild, TemplateRef, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FileService } from '../../services/file.service';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { PopupService } from '../../services/popup.service';
import { ModalService } from '../../services/modal.service';
import { finalize, map, switchMap, take, throwError } from 'rxjs';
import { Arquivo } from 'src/app/core/models/arquivo.model';
import { ModalComponent } from '../modal/modal.component';

/*
 * Este componente não envia os arquivos para o backend, apenas edita a lista de arquivos do componente que o utilizar!
 * O envio da lista de arquivos para o backend deverá ser realizado pelo fileService no component utilizador.
 */

@Component({
  selector: 'app-file-upload-selector',
  templateUrl: './file-upload-selector.component.html',
  styleUrls: ['./file-upload-selector.component.css'],
})
export class FileUploadSelectorComponent implements OnInit {
  private readonly mimeToExtensionsMap: Record<string, string[]> = {
    'application/pdf': ['.pdf'],
    'image/jpeg': ['.jpg', '.jpeg'],
    'image/png': ['.png'],
    'image/webp': ['.webp'],
    'application/msword': ['.doc'],
    'application/vnd.ms-excel': ['.xls'],
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
  };

  @Input() accent?: AppAccent;
  private readonly visualContext = inject(AppVisualContextService);
  @HostBinding('class') get visualClass(): string {
    return this.accent === undefined ? '' : this.visualContext.classForInputs(this.accent);
  }
  @Input({ required: true }) title: string; // Título da Seção Arquivos
  @Input({ required: true }) files: Arquivo[];
  @Input({ required: true }) filesControl?: AbstractControl;
  @Input() allowedMimes: string[] = [
    'application/pdf',
    'image/jpeg',
    'image/png',
    'image/webp',
    'application/msword',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  ];
  // Quando informado, cada arquivo adicionado abre um modal para o usuário escolher o tipo de documento, que vira o título do arquivo.
  @Input() tiposDocumento: string[] = [];

  readonly MAX_AMOUNT_FILES: number = 20; // Limite de arquivos
  readonly MAX_SIZE_FILE_IN_BYTES = 1024 * 1024 * 20; // 20MB. Tamanho máximo por arquivo
  readonly MAX_TOTAL_SIZE_IN_BYTES = 120 * 1024 * 1024; // 120MB. Tamanho máximo total de todos os arquivos

  protected required: boolean;
  protected fileSelected: Arquivo | null = null;
  protected fileInfoForm: FormGroup;
  protected readonly TIPO_DOCUMENTO_OUTRO = 'Outro';
  protected arquivoTipoDocumento: Arquivo | null = null;
  protected tipoDocumentoForm: FormGroup;
  private filaTipoDocumento: Arquivo[] = [];

  @ViewChild('modalEditInfo') modalEditInfo: TemplateRef<ModalComponent>;
  @ViewChild('modalDeleteFile') modalDeleteFile: TemplateRef<ModalComponent>;
  @ViewChild('modalDeleteAllFiles') modalDeleteAllFiles: TemplateRef<ModalComponent>;
  @ViewChild('modalTipoDocumento') modalTipoDocumento: TemplateRef<ModalComponent>;

  constructor(
    private fileService: FileService,
    private formBuilder: FormBuilder,
    private modalService: ModalService,
    private popupService: PopupService,
    private destroyRef: DestroyRef
  ) {}

  ngOnInit(): void {
    this.buildForm();
    this.required = this.filesControl?.hasValidator(Validators.required) ?? false;
  }

  buildForm() {
    this.fileInfoForm = this.formBuilder.group({
      titulo: [null],
      descricao: [null],
    });
    this.fileInfoForm.get('titulo')?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(value => {
        this.fileInfoForm.get('titulo')?.setValue(value.toUpperCase(), { emitEvent: false });
      });

    this.tipoDocumentoForm = this.formBuilder.group({
      tipo: [null, Validators.required],
      outro: [''],
    });
    this.tipoDocumentoForm.get('tipo')?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(tipo => {
        const outro = this.tipoDocumentoForm.get('outro');
        outro?.setValidators(tipo === this.TIPO_DOCUMENTO_OUTRO ? [Validators.required, Validators.pattern(/\S/)] : null);
        outro?.updateValueAndValidity({ emitEvent: false });
      });
  }

  fileSizeValid(fileSize: number): boolean {
    return fileSize <= this.MAX_SIZE_FILE_IN_BYTES;
  }

  protected get allowedExtensions(): string[] {
    const extensions = this.allowedMimes.flatMap(mime => this.mimeToExtensionsMap[mime] ?? []);
    return [...new Set(extensions)];
  }

  protected get acceptAttribute(): string {
    return this.allowedExtensions.join(', ');
  }

  protected get allowedExtensionsTooltip(): string {
    return `Extensões permitidas: ${this.allowedExtensions.join(', ')} com tamanho máximo de ${this.MAX_SIZE_FILE_IN_BYTES / 1024 / 1024}MB.`;
  }

  onLoadFiles(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files?.length) {
      let loadedFilesList: FileList = input.files as FileList;
      let processedFiles = 0;

      if (this.files.length == this.MAX_AMOUNT_FILES) {
        this.popupService.showError(`Limite de ${this.MAX_AMOUNT_FILES} arquivos atingido!`);
        input.value = ''; // Reset do input
        return;
      }

      const maxFilesProcess =
        loadedFilesList.length > this.MAX_AMOUNT_FILES - this.files.length ? this.MAX_AMOUNT_FILES - this.files.length : loadedFilesList.length;

      for (let i = 0; i < maxFilesProcess; ++i) {
        let file = loadedFilesList[i];
        // Verifica o tamanho do arquivo
        if (!this.fileSizeValid(file.size)) {
          this.popupService.showError(
            `O arquivo ${file.name} é maior que o limite de ${this.MAX_SIZE_FILE_IN_BYTES / 1024 / 1024}MB!` +
              (i < loadedFilesList.length && loadedFilesList.length > 1 ? '\nVerificando o próximo arquivo!' : '')
          );
          if (i == loadedFilesList.length - 1) {
            input.value = ''; // Reset do input caso seja o último
          }
          continue; // Pula para a próxima iteração do loop, ignorando o arquivo atual
        }
        // Estimativa total atual
        let estimatedCurrentTotalSize = this.getCurrentTotalSizeEstimate();
        let estimatedNewFileSize = Math.ceil(file.size * 1.33); // estimativa base64
        let newEstimatedTotalSize = estimatedCurrentTotalSize + estimatedNewFileSize;

        if (newEstimatedTotalSize > this.MAX_TOTAL_SIZE_IN_BYTES) {
          this.popupService.showError(
            `O arquivo ${file.name} ultrapassa o limite total de ${this.MAX_TOTAL_SIZE_IN_BYTES / 1024 / 1024}MB!` +
              (i < loadedFilesList.length && loadedFilesList.length > 1 ? '\nVerificando o próximo arquivo!' : '')
          );
          continue;
        }

        this.fileService
          .detectMimeFromBytes(file)
          .pipe(
            take(1),
            switchMap(mimeDetectado => {
              if (!this.allowedMimes.includes(mimeDetectado)) {
                return throwError(() => new Error(this.getInvalidMimeMessage(file.name, mimeDetectado)));
              }

              return this.fileService.convertFileToBase64(file).pipe(
                take(1),
                map(resultBase64 => ({ mimeDetectado, resultBase64 }))
              );
            }),
            finalize(() => {
              processedFiles++;
              if (processedFiles === maxFilesProcess) {
                if (maxFilesProcess < loadedFilesList.length) {
                  this.popupService.showError(`A quantidade máxima permitida é de ${this.MAX_AMOUNT_FILES} arquivos!`);
                }
                input.value = '';
              }
            })
          )
          .subscribe({
            next: ({ mimeDetectado, resultBase64 }) => {
              const isDuplicate = this.files.some(item => item.BASE64 === resultBase64);
              if (isDuplicate) {
                this.popupService.showAlert(
                  `O arquivo ${file.name} já foi carregado!` + (i < loadedFilesList.length && loadedFilesList.length > 1 ? '\nVerificando o próximo arquivo!' : '')
                );
                return;
              }

              const arquivo: Arquivo = {
                ARQUIVO: file.name,
                TITULO: this.fileService.removeFileExtension(file.name).toUpperCase(),
                TIPO: mimeDetectado,
                BASE64: resultBase64,
              };
              this.files.push(arquivo);
              this.syncFilesControl();
              if (this.tiposDocumento.length) {
                this.filaTipoDocumento.push(arquivo);
                this.abrirProximoTipoDocumento();
              }
            },
            error: error => {
              this.popupService.showError(error?.message || `Erro ao validar o arquivo ${file.name}.`);
            },
          });
      }
    }
  }

  getExtensionImage64(type: string): string {
    const extensionImage64: Record<string, string> = {
      'image/png': 'data:image/png;base64,',
      'image/jpg': 'data:image/jpg;base64,',
      'image/jpeg': 'data:image/jpeg;base64,',
      'image/webp': 'data:image/webp;base64,',
    };
    return extensionImage64[type];
  }

  openModalEditInfo(file: Arquivo) {
    this.fileSelected = file;
    this.fileInfoForm.patchValue({
      titulo:
        this.fileSelected.TITULO.toLowerCase() === this.fileService.removeFileExtension(this.fileSelected.ARQUIVO).toLowerCase()
          ? ''
          : this.fileSelected.TITULO,
      descricao: this.fileSelected.DESCRICAO,
    });
    let modal = this.modalService.openModalMedium(this.modalEditInfo);

    modal
      .afterClosed()
      .pipe(take(1))
      .subscribe((result: boolean) => {
        if (result) {
          let titulo = this.fileService.removeFileExtension(this.fileInfoForm.get('titulo')?.value ?? '');
          let descricao = this.fileInfoForm.get('descricao')?.value ?? '';
          let index = this.files.findIndex(file => file.BASE64 === this.fileSelected?.BASE64);
          index !== -1
            ? ((this.files[index].TITULO = titulo.trim() !== '' ? titulo : this.fileSelected?.TITULO ?? ''),
              (this.files[index].DESCRICAO = descricao),
              this.syncFilesControl(),
              this.popupService.showSuccess('Informações alteradas com sucesso!'))
            : this.popupService.showError('Erro ao editar informações!');
          this.fileSelected = null;
          this.fileInfoForm.get('titulo')?.setValue('');
          this.fileInfoForm.get('descricao')?.setValue('');
        }
      });
  }
  // Os arquivos de uma mesma seleção são classificados um de cada vez; cancelar mantém o nome original.
  private abrirProximoTipoDocumento(): void {
    if (this.arquivoTipoDocumento) {
      return;
    }
    const arquivo = this.filaTipoDocumento.shift();
    if (!arquivo) {
      return;
    }

    this.arquivoTipoDocumento = arquivo;
    this.tipoDocumentoForm.reset({ tipo: null, outro: '' });
    this.modalService
      .openModalMedium(this.modalTipoDocumento)
      .afterClosed()
      .pipe(take(1))
      .subscribe((result: boolean) => {
        if (result && this.files.includes(arquivo)) {
          const { tipo, outro } = this.tipoDocumentoForm.value;
          arquivo.TITULO = (tipo === this.TIPO_DOCUMENTO_OUTRO ? outro : tipo).trim().toUpperCase();
          this.syncFilesControl();
        }
        this.arquivoTipoDocumento = null;
        this.abrirProximoTipoDocumento();
      });
  }

  deleteFile(file: Arquivo) {
    this.fileSelected = file;
    let modal = this.modalService.openModalSmall(this.modalDeleteFile);

    modal
      .afterClosed()
      .pipe(take(1))
      .subscribe((result: boolean) => {
        if (result) {
          let index = this.files.indexOf(file);
          index !== -1
            ? (this.files.splice(index, 1), this.syncFilesControl(), this.popupService.showSuccess('Arquivo excluído com sucesso!'))
            : this.popupService.showError('Erro ao excluir arquivo!');
        }
      });
  }

  deleteAllFiles() {
    let modal = this.modalService.openModalSmall(this.modalDeleteAllFiles);

    modal
      .afterClosed()
      .pipe(take(1))
      .subscribe((result: boolean) => {
        if (result) {
          this.files = [];
          this.syncFilesControl();
          this.popupService.showSuccess('Todos os arquivos foram excluídos com sucesso!');
        }
      });
  }

  private getCurrentTotalSizeEstimate(): number {
    return this.files.reduce((total, file: Arquivo) => {
      const base64Length = file.BASE64?.length || 0;
      // Aproximação: base64 é ~33% maior que o binário original
      return total + Math.floor((base64Length * 3) / 4);
    }, 0);
  }

  private syncFilesControl(): void {
    if (this.filesControl) {
      this.filesControl.setValue([...this.files]);
    }
  }

  private getInvalidMimeMessage(fileName: string, mimeDetectado: string): string {
    const mimeLabel = mimeDetectado === 'application/octet-stream' ? 'desconhecido' : mimeDetectado;
    return `O arquivo ${fileName} tem um tipo não permitido (${mimeLabel}). Tipos aceitos: ${this.allowedExtensions.join(', ')}.`;
  }

}

